import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { DepartmentStatus } from '@21k/shared';

import { getApiErrorMessage, listManageDepartments } from '../../api/client';
import type { DepartmentDetail } from '../../api/types';
import Badge from '../../components/Badge';
import { useAuth } from '../../contexts/AuthContext';
import { canEditDepartments, canWriteDepartments } from '../../utils/permissions';
import { PLATFORM_CONFIG } from '../platform-config/paths';

export default function DepartmentsPage() {
  const { user } = useAuth();
  const [departments, setDepartments] = useState<DepartmentDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const canEdit = user && canWriteDepartments(user);

  useEffect(() => {
    listManageDepartments()
      .then(setDepartments)
      .catch((err) => setError(getApiErrorMessage(err)))
      .finally(() => setLoading(false));
  }, []);

  return (
    <>
      <div className="page-header" style={{ marginTop: 0 }}>
        <div>
          <p style={{ margin: '0.25rem 0 0', color: 'var(--text-muted)', fontSize: '0.9rem' }}>
            Support queues — CX, internal, school, and program teams.
          </p>
        </div>
        {canEdit && (
          <Link to={PLATFORM_CONFIG.departmentsNew} className="btn btn-primary">
            Add department
          </Link>
        )}
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="card">
        {loading ? (
          <div className="loading">Loading departments…</div>
        ) : (
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Members</th>
                  <th>Tickets</th>
                  <th>SLA rules</th>
                  <th>Channels</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {departments.map((d) => (
                  <tr key={d.id}>
                    <td>
                      <Link to={PLATFORM_CONFIG.department(d.id)}>{d.name}</Link>
                      {d.erpReferenceId ? (
                        <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                          ERP {d.erpReferenceType}: {d.erpReferenceId}
                        </div>
                      ) : null}
                    </td>
                    <td>
                      <Badge label={d.type} />
                    </td>
                    <td>
                      <Badge
                        label={d.status}
                        className={
                          d.status === DepartmentStatus.ACTIVE ? 'badge-success' : 'badge-neutral'
                        }
                      />
                    </td>
                    <td>{d._count.members}</td>
                    <td>{d._count.tickets}</td>
                    <td>{d._count.slaRules}</td>
                    <td>{d._count.channelConfigs}</td>
                    <td>
                      <Link to={PLATFORM_CONFIG.departmentTeams(d.id)} className="btn btn-secondary btn-sm">
                        Teams
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
